import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { TopicNode } from "@/types/nodeTypes"
import { TopicEdge } from "@/types/edgeTypes"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Filter nodes by search term
 * @param nodes The nodes to filter
 * @param searchTerm The search term to filter by
 * @returns Filtered nodes
 */
export function filterNodesBySearchTerm(nodes: TopicNode[], searchTerm: string): TopicNode[] {
  if (!searchTerm) return nodes;
  
  const lowerCaseSearchTerm = searchTerm.toLowerCase();
  return nodes.filter(node => {
    // Check title
    if (node.title.toLowerCase().includes(lowerCaseSearchTerm)) {
      return true;
    }
    
    // Check content string (for backward compatibility)
    if (node.content && node.content.toLowerCase().includes(lowerCaseSearchTerm)) {
      return true;
    }
    
    // Check contents array
    if (node.contents && node.contents.length > 0) {
      return node.contents.some(item => 
        item.content && item.content.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
    
    return false;
  });
}

/**
 * Filter nodes by phase
 * @param nodes The nodes to filter
 * @param phaseIds The phase IDs to filter by
 * @returns Filtered nodes
 */
export function filterNodesByPhase(nodes: TopicNode[], phaseIds: string[]): TopicNode[] {
  if (!phaseIds.length) return nodes;
  
  return nodes.filter(node => phaseIds.includes(node.phaseId));
}

/**
 * Filter nodes by importance
 * @param nodes The nodes to filter
 * @param minImportance The minimum importance level
 * @returns Filtered nodes
 */
export function filterNodesByImportance(nodes: TopicNode[], minImportance: number): TopicNode[] {
  if (minImportance <= 1) return nodes;
  
  return nodes.filter(node => node.importance >= minImportance);
}

/**
 * Filter nodes by active state
 * @param nodes The nodes to filter
 * @param showInactive Whether to include inactive nodes
 * @returns Filtered nodes
 * @deprecated Use explicit filterVisibility instead
 */
export function filterNodesByActive(nodes: TopicNode[], showInactive: boolean = false): TopicNode[] {
  if (showInactive) return nodes;
  
  return nodes.filter(node => node.active);
}

/**
 * Filter edges by nodes
 * @param edges The edges to filter
 * @param nodeIds The node IDs to filter by
 * @returns Filtered edges
 */
export function filterEdgesByNodes(edges: TopicEdge[], nodeIds: string[]): TopicEdge[] {
  return edges.filter(edge => 
    nodeIds.includes(edge.source) && nodeIds.includes(edge.target)
  );
}

/**
 * Filter edges by active state
 * @param edges The edges to filter
 * @param showInactive Whether to include inactive edges
 * @returns Filtered edges
 * @deprecated Use explicit filterVisibility instead
 */
export function filterEdgesByActive(edges: TopicEdge[], showInactive: boolean = false): TopicEdge[] {
  if (showInactive) return edges;
  
  return edges.filter(edge => edge.active);
}

/**
 * Get nodes related to a specific node
 * @param nodeId The ID of the node to get related nodes for
 * @param nodes All nodes
 * @param edges All edges
 * @returns Related nodes
 */
export function getRelatedNodes(nodeId: string, nodes: TopicNode[], edges: TopicEdge[]): TopicNode[] {
  // Find all edges connected to this node
  const connectedEdges = edges.filter(edge => 
    edge.source === nodeId || edge.target === nodeId
  );
  
  // Get the IDs of connected nodes
  const connectedNodeIds = connectedEdges.map(edge => 
    edge.source === nodeId ? edge.target : edge.source
  );
  
  // Return the connected nodes
  return nodes.filter(node => connectedNodeIds.includes(node.id));
}

/**
 * Get CSS class based on importance level
 * @param importance The importance level (1-5)
 * @returns CSS class for styling
 */
export function getImportanceClass(importance: number): string {
  switch (importance) {
    case 5:
      return "scale-110";
    case 4:
      return "scale-105";
    case 3:
      return "scale-100";
    case 2:
      return "scale-95";
    case 1:
    default:
      return "scale-90";
  }
}

/**
 * Update node positions based on layout
 * @param nodes The nodes to update
 * @param positions Record of node positions
 * @returns Updated nodes with new positions
 */
export function updateNodePositions(
  nodes: TopicNode[],
  positions: Record<string, { x: number, y: number }>
): TopicNode[] {
  return nodes.map(node => {
    const position = positions[node.id];
    if (position) {
      return {
        ...node,
        position
      };
    }
    return node;
  });
}

/**
 * Parse timestamp string to seconds
 * @param timestamp Timestamp string in format HH:MM:SS
 * @returns Number of seconds
 */
export function timestampToSeconds(timestamp: string): number {
  const parts = timestamp.split(':').map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  return 0;
}

/**
 * Convert seconds to timestamp string
 * @param seconds Number of seconds
 * @returns Timestamp string in format HH:MM:SS
 */
export function secondsToTimestamp(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  return [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    secs.toString().padStart(2, '0')
  ].join(':');
}

/**
 * Generate a unique ID
 * @returns Unique ID string
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

/**
 * Get CSS transition class based on animation enabled state
 * @param animateEnabled Whether animation is enabled
 * @returns CSS transition class
 */
export function getTransitionClass(animateEnabled: boolean): string {
  return animateEnabled 
    ? "transition-all duration-500 ease-in-out" 
    : "transition-none";
}